// Fiche d'une attribution : commerçant, emplacement, période, décision ; échéancier des redevances
// avec les créances émises (numéro, montant au franc près, règle, échéance, origine) ; résiliation
// motivée avec date d'effet et renouvellement par le responsable des marchés.
import { CODES_PERIODICITES } from '@cc/contrats';
import { Bandeau, Bouton, DateChamp, DialogueConfirmation, Lien, LienBouton, Panneau, PuceEtat, Selection, ZoneTexte } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_MARCHES, renouvelerAttribution, resilierAttribution, useAttribution } from './api';
import { aujourdhuiIso, LIBELLES_CANAL, LIBELLES_PERIODICITE, LIBELLES_SITUATION, libelleCommercant, montant } from './logique';
import { TON_SITUATION } from './PageAttributions';
import type { AttributionDetail, PeriodiciteRedevance } from './types';
import styles from './marches.module.css';
import { CarteCommercant } from './CarteCommercant';

function Resiliation({ a }: { a: AttributionDetail }) {
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [dateEffet, setDateEffet] = useState(aujourdhuiIso());
  const action = useAction({
    executer: () => resilierAttribution(a.id, { motif: motif.trim(), date_effet: dateEffet || undefined }),
    succes: (r) => `Attribution résiliée${r.resiliation?.creances_annulees ? `, ${r.resiliation.creances_annulees} créance${r.resiliation.creances_annulees > 1 ? 's' : ''} non payée${r.resiliation.creances_annulees > 1 ? 's' : ''} annulée${r.resiliation.creances_annulees > 1 ? 's' : ''}` : ''}.`,
    invalider: INVALIDER_MARCHES,
    apres: () => setOuvert(false),
  });
  return (
    <>
      <Bouton variante="danger" onClick={() => setOuvert(true)}>
        Résilier
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre={`Résilier l'attribution ${a.numero}`}
        message={<p>L&apos;emplacement est libre à partir de la date d&apos;effet ; les créances non payées de l&apos;attribution sont annulées par le module des recettes avec ce motif.</p>}
        libelleConfirmer="Résilier"
        variante="danger"
        enCours={action.enCours}
        confirmationDesactivee={motif.trim().length < 5}
        onConfirmer={() => action.lancer(undefined)}
        onAnnuler={() => {
          setOuvert(false);
          action.reinitialiser();
        }}
      >
        <ZoneTexte libelle="Motif (cinq caractères au moins)" value={motif} onChange={(e) => setMotif(e.target.value)} required erreur={action.erreursChamps.find((c) => c.champ === 'motif')?.message} />
        <DateChamp libelle="Date d'effet" value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} erreur={action.erreursChamps.find((c) => c.champ === 'date_effet')?.message} />
        {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

function Renouvellement({ a }: { a: AttributionDetail }) {
  const naviguer = useNavigate();
  const [ouvert, setOuvert] = useState(false);
  const [fin, setFin] = useState('');
  const [periodicite, setPeriodicite] = useState('');
  const [reference, setReference] = useState('');
  const action = useAction({
    executer: () => renouvelerAttribution(a.id, { fin: fin || undefined, periodicite: (periodicite || undefined) as PeriodiciteRedevance | undefined, decision_reference: reference || undefined }),
    succes: (r) => `Attribution renouvelée sous le numéro ${r.numero}.`,
    invalider: INVALIDER_MARCHES,
    apres: (r) => void naviguer(`/marches/attributions/${r.id}`),
  });
  return (
    <>
      <Bouton variante="principal" onClick={() => setOuvert(true)}>
        Renouveler
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre={`Renouveler l'attribution ${a.numero}`}
        message={<p>Une nouvelle attribution contiguë commence le lendemain de la fin, pour la même durée par défaut ; une attribution ne se renouvelle qu&apos;une fois.</p>}
        libelleConfirmer="Renouveler"
        enCours={action.enCours}
        onConfirmer={() => action.lancer(undefined)}
        onAnnuler={() => {
          setOuvert(false);
          action.reinitialiser();
        }}
      >
        <DateChamp libelle="Fin de la nouvelle période" value={fin} onChange={(e) => setFin(e.target.value)} facultatif aide="Vide : même durée que la période renouvelée" erreur={action.erreursChamps.find((c) => c.champ === 'fin')?.message} />
        <Selection libelle="Périodicité" optionVide="Inchangée" options={CODES_PERIODICITES.map((p) => ({ valeur: p, libelle: LIBELLES_PERIODICITE[p] }))} value={periodicite} onChange={(e) => setPeriodicite(e.target.value)} />
        <ZoneTexte libelle="Référence de la décision" value={reference} onChange={(e) => setReference(e.target.value)} facultatif />
        {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

export function PageAttribution() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const attribution = useAttribution(id);
  const a = attribution.data;
  const enVigueur = a?.etat === 'attribuee' || a?.etat === 'active';
  return (
    <>
      <EnTetePage
        etiquette={a ? `${a.marche?.nom ?? 'Marché'} · emplacement ${a.emplacement.reference ?? ''}` : 'Attribution'}
        titre={a ? `Attribution ${a.numero}` : 'Attribution'}
        description={a ? <span>{libelleCommercant(a)}{a.activite ? `, ${a.activite}` : ''}. Du {date(a.periode.debut)} au {date(a.periode.fin)}, redevance {LIBELLES_PERIODICITE[a.periodicite as PeriodiciteRedevance]?.toLowerCase() ?? a.periodicite}.</span> : null}
        actions={
          <>
            <LienBouton href={a ? `/marches/attributions${a.marche ? `?marche=${a.marche.id}` : ''}` : '/marches/attributions'} icone={ArrowLeft}>
              Attributions
            </LienBouton>
            {a && enVigueur && peut(contexte, 'marche_municipal', 'creer') ? <Renouvellement a={a} /> : null}
            {a && enVigueur && peut(contexte, 'marche_municipal', 'modifier') ? <Resiliation a={a} /> : null}
            {a && enVigueur && peut(contexte, 'marche_municipal', 'exporter') ? <CarteCommercant attributionId={a.id} /> : null}
          </>
        }
      />
      <EtatRequete chargement={attribution.isPending} erreur={attribution.error} onReessayer={() => void attribution.refetch()} lot="L2-B" fonction="La fiche de l'attribution">
        {a ? (
          <div className={styles.disposition}>
            <div className={styles.pile}>
              <Panneau titre="Échéancier des redevances" niveau={3}>
                <p className={styles.aide}>
                  Une créance par période de redevance, calculée et émise par le module des recettes ; les périodes à venir sont émises par la tâche quotidienne à leur début.
                </p>
                <table className={`${styles.tableauPlan} ${styles.echeancier}`}>
                  <caption className="cc-masque">Échéancier des redevances</caption>
                  <thead>
                    <tr>
                      <th scope="col">Période</th>
                      <th scope="col">Créance</th>
                      <th scope="col">Montant</th>
                      <th scope="col">Règle</th>
                      <th scope="col">Échéance</th>
                      <th scope="col">Origine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.echeancier.map((p) => {
                      const r = a.redevances.find((x) => x.rang === p.rang);
                      return (
                        <tr key={p.rang}>
                          <th scope="row">
                            {p.rang}. du {date(p.debut)} au {date(p.fin)}
                          </th>
                          {r ? (
                            <>
                              <td className="cc-mono">{r.creance.numero}</td>
                              <td className={styles.montant}>{montant(r.creance.montant_du)}</td>
                              <td className={styles.regle}>{r.creance.regle}</td>
                              <td>{date(r.echeance)}</td>
                              <td>{r.origine === 'tache' ? 'tâche quotidienne' : r.origine === 'attribution' ? 'acte d\'attribution' : r.origine}</td>
                            </>
                          ) : (
                            <td colSpan={5} data-vide>
                              {a.etat === 'resiliee' ? 'non émise (attribution résiliée)' : 'à émettre au début de la période'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Panneau>
              {a.resiliation ? (
                <Bandeau type="attention" titre="Attribution résiliée">
                  Motif : {a.resiliation.motif}. Date d&apos;effet : {date(a.resiliation.date_effet)}
                  {a.resiliation.resiliee_le ? `, décidée le ${dateHeure(a.resiliation.resiliee_le)}` : ''}
                  {a.resiliation.creances_annulees ? `, ${a.resiliation.creances_annulees} créance${a.resiliation.creances_annulees > 1 ? 's' : ''} annulée${a.resiliation.creances_annulees > 1 ? 's' : ''}` : ''}.
                </Bandeau>
              ) : null}
            </div>
            <aside className={styles.cote} aria-label="Situation de l'attribution">
              <Panneau titre="Situation" niveau={3}>
                <dl className={styles.definitions}>
                  <dt>Situation</dt>
                  <dd>
                    <PuceEtat ton={TON_SITUATION[a.situation] ?? 'neutre'} libelle={LIBELLES_SITUATION[a.situation]} />
                    {a.periode_effective && a.periode_effective.fin !== a.periode.fin ? ` (effective jusqu'au ${date(a.periode_effective.fin)})` : ''}
                  </dd>
                  <dt>Emplacement</dt>
                  <dd>
                    <Lien href={`/marches/emplacements/${a.emplacement.id}`} className="cc-mono">
                      {a.emplacement.reference ?? a.emplacement.id}
                    </Lien>
                    {a.emplacement.type ? ` · ${a.emplacement.type}` : ''} · {a.surface_m2.toLocaleString('fr-FR')} m²
                  </dd>
                  {a.marche ? (
                    <>
                      <dt>Marché</dt>
                      <dd>
                        <Lien href={`/marches/${a.marche.id}?emplacement=${a.emplacement.id}`}>{a.marche.nom ?? 'Plan'}</Lien>
                      </dd>
                    </>
                  ) : null}
                  <dt>Commerçant</dt>
                  <dd>
                    {libelleCommercant(a)}
                    {a.commercant.telephone ? `, ${a.commercant.telephone}` : ''}
                  </dd>
                  <dt>Origine</dt>
                  <dd>
                    {LIBELLES_CANAL[a.canal] ?? a.canal}
                    {a.dossier_id ? (
                      <>
                        {' · '}
                        <Lien href={`/dossiers/${a.dossier_id}`}>dossier de la demande</Lien>
                      </>
                    ) : null}
                    {a.renouvelle_id ? (
                      <>
                        {' · '}
                        <Lien href={`/marches/attributions/${a.renouvelle_id}`}>attribution renouvelée</Lien>
                      </>
                    ) : null}
                  </dd>
                  <dt>Rubrique</dt>
                  <dd className="cc-mono">{a.rubrique_code}</dd>
                  <dt>Décision</dt>
                  <dd>
                    {a.decision.reference ?? 'sans référence'}
                    {a.decision.decide_le ? `, le ${dateHeure(a.decision.decide_le)}` : ''}
                  </dd>
                  {a.renouvellement_propose_le ? (
                    <>
                      <dt>Renouvellement</dt>
                      <dd>proposé le {dateHeure(a.renouvellement_propose_le)}</dd>
                    </>
                  ) : null}
                  {a.observations ? (
                    <>
                      <dt>Observations</dt>
                      <dd>{a.observations}</dd>
                    </>
                  ) : null}
                </dl>
              </Panneau>
            </aside>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
